import {
  BulkActionSuccess,
  CreatedReportAction,
  DeletedReportAction,
  DuplicatedReportAction,
  ReportActions,
  ReportActionTypes,
  ToggleSelectAllReportsAction,
  ToggleSelectReportAction, UpdatedReportsAction,
} from '../actions/reportActions';
import {CreatedTagAction, RemovedTagAction, TagsActionTypes,} from '../actions/tagActions';
import {LoadedReportResultAction, ReportResultActionTypes,} from '../actions/reportResultActions';
import {ApiStatus, Report,} from '../../models';

export const initialReportState: ReportState = {
  loadingStatus: null,
  creatingStatus: null,
  deletingStatus: null,
  updatingStatus: null, // bulk update
  reports: null,
  canLoadMore: false,
}

export interface ReportState {
  loadingStatus: ApiStatus | null;
  creatingStatus: ApiStatus | null,
  deletingStatus: ApiStatus | null;
  updatingStatus: ApiStatus | null; // bulk update
  reports: Report[] | null;
  canLoadMore: boolean;
}

export default function reportReducer(
  state: ReportState = initialReportState,
  action:
    ReportActions |
    LoadedReportResultAction |
    CreatedTagAction |
    RemovedTagAction
): ReportState {
  switch (action.type) {
    case ReportActionTypes.LOAD_REPORTS:
      return {
        ...state,
        loadingStatus: ApiStatus.LOADING,
      }

    case ReportActionTypes.LOADING_REPORTS_FAILED:
      return {
        ...state,
        loadingStatus: ApiStatus.FAILED,
      }
    case ReportActionTypes.LOADED_REPORTS:
      const newReports = action.payload.result || [];
      const reload = action.payload.reload || false;
      const page_size = action.payload.page_size || 0;

      return {
        ...state,
        loadingStatus: ApiStatus.LOADED,
        reports: reload ? [...newReports] : [
          ...state.reports || [],
          ...newReports,
        ],
        canLoadMore: newReports.length === page_size,
      }

    case ReportActionTypes.UNSET_REPORTS:
      return {
        ...state,
        reports: null,
        canLoadMore: false,
        loadingStatus: null,
        creatingStatus: null,
        updatingStatus: null,
      }

    case ReportActionTypes.CREATE_REPORT:
    case ReportActionTypes.DUPLICATE_REPORT:
      return {
        ...state,
        creatingStatus: ApiStatus.LOADING,
      }
    case ReportActionTypes.CREATED_REPORT:
    case ReportActionTypes.DUPLICATED_REPORT:
      return handleCreateReport(state, action);
    case ReportActionTypes.CREATING_REPORT_FAILED:
    case ReportActionTypes.DUPLICATING_REPORT_FAILED:
      return {
        ...state,
        creatingStatus: ApiStatus.FAILED,
      }

    case ReportActionTypes.DELETE_REPORT:
      return {
        ...state,
        deletingStatus: ApiStatus.LOADING,
      }
    case ReportActionTypes.DELETING_REPORT_FAILED:
      return {
        ...state,
        deletingStatus: ApiStatus.FAILED,
      }
    case ReportActionTypes.DELETED_REPORT:
      return handleDeleteReport(state, action);
    case ReportActionTypes.TOGGLE_SELECT_REPORT:
      return handleToggleSelectReport(state, action);
    case ReportActionTypes.TOGGLE_SELECT_ALL_REPORTS:
      return handleToggleSelectAllReports(state, action);
    case ReportActionTypes.BULK_ACTION_SUCCESS:
      return handleBulkAction(state, action);
    case ReportActionTypes.UPDATE_REPORTS:
      return {
        ...state,
        updatingStatus: ApiStatus.LOADING,
      };
    case ReportActionTypes.UPDATING_REPORTS_FAILED:
      return {
        ...state,
        updatingStatus: ApiStatus.FAILED,
      };
    case ReportActionTypes.UPDATED_REPORTS:
      return handleUpdateReports(state, action);
    /*
     * Report execution
     */
    case ReportResultActionTypes.LOADED_REPORT_RESULT:
      return handleUpdateLatestRuns(state, action);
    /*
     * Tags
     */
    case TagsActionTypes.CREATED_TAG:
      return handleCreatedTag(state, action);
    case TagsActionTypes.REMOVED_TAG:
      return handleRemovedTag(state, action);
    default:
      return state;
  }
}

// Functions

function handleCreateReport(
  state: ReportState,
  action: CreatedReportAction | DuplicatedReportAction
): ReportState {
  const reports = state.reports || [];

  return {
    ...state,
    reports: [
      action.payload,
      ...reports,
    ],
    creatingStatus: ApiStatus.LOADED,
  }
}

function handleDeleteReport(state: ReportState, action: DeletedReportAction) {
  return {
    ...state,
    reports: state.reports?.filter(report => report.uid !== action.payload) || [],
    deletingStatus: ApiStatus.LOADED,
  }
}

function handleBulkAction(state: ReportState, action: BulkActionSuccess): ReportState {
  const {action: bulkAction, selected} = action.payload;

  switch (bulkAction) {
    case 'delete':
      return {
        ...state,
        reports: state.reports?.filter(report => !selected.includes(report.uid as string) ) || [],
      }
    default:
      return state;
  }
}

function handleUpdateReports(state: ReportState, action: UpdatedReportsAction): ReportState {
  let newReports = state.reports || [];

  newReports =newReports.map((report: Report) => {
    const updatedReport = action.payload.find((r: Report) => r.uid === report.uid);
    if (updatedReport) {
      return {
        ...report,
        folder_id: updatedReport.folder_id, // TODO: handle other fields as well
        folder: updatedReport.folder,
      };
    }
    return report;
  });

  return {
    ...state,
    reports: newReports,
    updatingStatus: ApiStatus.LOADED,
  }
}

function handleToggleSelectReport(
  state: ReportState,
  action: ToggleSelectReportAction,
): ReportState {
  const { uid, selected } = action.payload;

  if (!state?.reports) {
    return state; // not on reports list page
  }

  const newReports = state.reports.map((report: Report) => {
    if (report.uid === uid) {
      return {
        ...report,
        selected: selected,
      };
    }
    return report;
  });

  return {
    ...state,
    reports: newReports,
  };
}

function handleToggleSelectAllReports(
  state: ReportState,
  action: ToggleSelectAllReportsAction,
): ReportState {
  const { selected } = action.payload;

  if (!state?.reports) {
    return state; // not on reports list page
  }

  const newReports = state.reports.map((report: Report) => ({
    ...report,
    selected: selected,
  }));

  return {
    ...state,
    reports: newReports,
  };
}

function handleUpdateLatestRuns(
  state: ReportState,
  action: LoadedReportResultAction,
): ReportState {
  /*
   * Handle only for reports list, as it is used only there (for now).
   */
  let newReports = state.reports || [];

  newReports = newReports.map((report: Report) => {
    if (action.payload.uid === report.uid) {
      const {result} = action.payload;
      return {
        ...report,
        latest_executions: [
          {
            id: result.id,
            uid: result.uid,
            status: result.status,
            start_time: result.created_on_utc,
          },
          ...report.latest_executions,
        ]
      }
    }
    return report;
  });

  return {
    ...state,
    reports: state.reports ? newReports : state.reports,
  }
}

function handleCreatedTag(state: ReportState, action: CreatedTagAction): ReportState {
  const {tag, parent} = action.payload;

  if (!parent || parent?.objectType !== 'report') {
    return state;
  } else if (!state?.reports) {
    return state; // not on reports list page
  }

  const newReports = state?.reports?.map((report: Report) => {
    if (report.uid === parent.objectId) {
      return {
        ...report,
        tags: [
          ...report.tags || [],
          tag,
        ],
      };
    }
    return report;
  }) || null;

  return {
    ...state,
    reports: newReports,
  }
}

function handleRemovedTag(state: ReportState, action: RemovedTagAction): ReportState {
  const {uid, parent} = action.payload;

  if (!!parent && parent.objectType !== 'report') {
    return state; // not removed from report
  } else if (!state?.reports) {
    return state; // not on reports list page
  }

  let newReports = state.reports;

  if (!!parent) {
    // If parent is provided, we need to filter the tags for the specific report
    newReports = state?.reports?.map((report: Report) => {
      if (report.uid === parent.objectId) {
        return {
          ...report,
          tags: report.tags?.filter((t: any) => t.uid !== uid) || [],
        };
      }
      return report;
    }) || null;
  } else {
    // If no parent is provided, we remove the tag from all reports
    newReports = state?.reports?.map((report: Report) => {
      if (!report.tags || report.tags.length === 0) {
        return report; // no tags to filter
      }

      return {
        ...report,
        tags: report.tags?.filter((t: any) => t.uid !== uid) || [],
      };
    }) || null;
  }

  return {
    ...state,
    reports: newReports,
  }
}

// Selectors
